/**
 * Shared vocabulary for the Linear-style violation views (#603 step 2):
 * the status and severity sets, how a violation list is grouped and
 * filtered, and how its calendar dates are read and written.
 *
 * Pure functions only, so the list, the detail page and the create modal
 * agree without importing each other, and so the rules are unit-testable.
 */
import { ViolationReturn } from '../../clients/rust-api-client';

export type ViolationStatus =
  | 'Pending'
  | 'InProgress'
  | 'Escalated'
  | 'Appealed'
  | 'Resolved';

/**
 * Display order, which is also group order in the list: open work first,
 * in roughly the order a manager acts on it, closed work last.
 * Values are the API's (`ViolationStatus`'s Display impl on the backend).
 */
export const STATUSES: { value: ViolationStatus; label: string }[] = [
  { value: 'InProgress', label: 'In Progress' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Escalated', label: 'Escalated' },
  { value: 'Appealed', label: 'Appealed' },
  { value: 'Resolved', label: 'Resolved' },
];

export const SEVERITIES = ['Critical', 'Major', 'Moderate', 'Minor'] as const;

export const VIOLATION_TYPES = [
  'Maintenance',
  'Noise',
  'Architectural',
  'Parking',
  'Landscaping',
  'Pets',
  'Trash',
  'Other',
] as const;

/** Normalises whatever casing the API or a caller used: 'in_progress' → 'InProgress'. */
export const normalizeStatus = (status: string): ViolationStatus | null => {
  const key = status.toLowerCase().replace(/[_ ]/g, '');
  const match = STATUSES.find((s) => s.value.toLowerCase() === key);
  return match ? match.value : null;
};

export const statusLabel = (status: string): string => {
  const s = normalizeStatus(status);
  return s ? STATUSES.find((x) => x.value === s)!.label : status;
};

/** Case-insensitive match against the known severities, keeping the canonical spelling. */
export const normalizeSeverity = (severity: string): string => {
  const match = SEVERITIES.find(
    (s) => s.toLowerCase() === severity.toLowerCase()
  );
  return match ?? severity;
};

// ---------------------------------------------------------------------------
// Grouping and filtering
// ---------------------------------------------------------------------------

export interface ViolationFilters {
  statuses: ViolationStatus[];
  severities: string[];
  types: string[];
  /** HOA ids (#618). A violation's HOA comes from its home, via `hoaOf`. */
  hoas: string[];
  search: string;
}

export const EMPTY_FILTERS: ViolationFilters = {
  statuses: [],
  severities: [],
  types: [],
  hoas: [],
  search: '',
};

/** Every non-empty filter must match ("match all filters"). */
export const applyFilters = (
  violations: ViolationReturn[],
  filters: ViolationFilters,
  /** A violation's HOA id. A violation whose HOA is unknown never matches an HOA filter. */
  hoaOf: (v: ViolationReturn) => string | undefined = () => undefined
): ViolationReturn[] => {
  const q = filters.search.trim().toLowerCase();
  return violations.filter((v) => {
    const status = normalizeStatus(v.status);
    if (filters.statuses.length && (!status || !filters.statuses.includes(status)))
      return false;
    if (
      filters.severities.length &&
      !filters.severities.includes(normalizeSeverity(v.severity))
    )
      return false;
    if (
      filters.types.length &&
      !filters.types.some((t) => t.toLowerCase() === v.violation_type.toLowerCase())
    )
      return false;
    if (filters.hoas.length) {
      const hoa = hoaOf(v);
      if (!hoa || !filters.hoas.includes(hoa)) return false;
    }
    if (
      q &&
      !v.description.toLowerCase().includes(q) &&
      !v.violation_type.toLowerCase().includes(q)
    )
      return false;
    return true;
  });
};

export interface ViolationGroup {
  status: ViolationStatus | 'Other';
  label: string;
  violations: ViolationReturn[];
}

/**
 * Groups by status in STATUSES order, newest violation first inside each
 * group. Empty groups are dropped. A status the UI does not know is not
 * hidden — it lands in a trailing "Other" group, because a list that
 * silently drops rows is worse than one with an odd heading.
 */
export const groupByStatus = (violations: ViolationReturn[]): ViolationGroup[] => {
  const byNewest = [...violations].sort(
    (a, b) =>
      Date.parse(b.date_of_violation) - Date.parse(a.date_of_violation)
  );
  const groups: ViolationGroup[] = STATUSES.map((s) => ({
    status: s.value,
    label: s.label,
    violations: byNewest.filter((v) => normalizeStatus(v.status) === s.value),
  }));
  const other = byNewest.filter((v) => normalizeStatus(v.status) === null);
  if (other.length) groups.push({ status: 'Other', label: 'Other', violations: other });
  return groups.filter((g) => g.violations.length > 0);
};

// ---------------------------------------------------------------------------
// Calendar dates
// ---------------------------------------------------------------------------
//
// A due date is a calendar day, but the API stores and returns an instant
// (`2026-03-13T04:34:16Z`) and accepts one back as epoch **milliseconds**.
// Reading that instant in the browser's zone and in UTC gives different
// days for anyone west of Greenwich in the evening UTC hours, which is how
// the old detail page and its edit form disagreed by a day.
//
// Here a date is always read by its UTC calendar day, and a picked day is
// always written as **noon UTC** of that day. Noon UTC falls on the same
// calendar day in every US zone, so older screens that still read the
// instant in local time agree with anything written from here.

/** 'YYYY-MM-DD' of the instant's UTC calendar day, or '' if absent/invalid. */
export const utcDay = (value: string | null | undefined): string => {
  if (!value) return '';
  const t = Date.parse(value);
  if (Number.isNaN(t)) return '';
  return new Date(t).toISOString().slice(0, 10);
};

/** 'Mar 13' (or 'Mar 13, 2025' outside the current year) for a stored instant. */
export const formatDay = (
  value: string | null | undefined,
  now: Date = new Date()
): string => {
  const day = utcDay(value);
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12));
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(y !== now.getUTCFullYear() ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
};

/** Epoch milliseconds for noon UTC on a 'YYYY-MM-DD' day — what the API expects. */
export const noonUtcMillis = (day: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  const check = new Date(t);
  // Reject 2026-02-31 and friends rather than letting Date roll them over.
  return check.getUTCDate() === Number(m[3]) ? t : null;
};

/**
 * What an edit form sends for one date field: noon-UTC milliseconds when
 * the day moved, else `undefined`, so the field stays out of the update.
 *
 * `edited` is the form's value, a 'YYYY-MM-DD' day or the stored instant
 * the form was seeded with; `current` is the stored instant. Comparing
 * days rather than values means an untouched field is never resent (#606:
 * the edit dialog used to resend both dates on every save, in seconds,
 * moving them to 1970). A cleared field also sends nothing: the API reads
 * `null` as "unchanged", so there is no way to remove a date.
 */
export const changedDayMillis = (
  edited: string | null | undefined,
  current: string | null | undefined
): number | undefined => {
  // A picked day goes to noonUtcMillis as typed: Date.parse would roll
  // 2026-02-31 over to March 3 rather than refuse it.
  const day =
    edited && /^\d{4}-\d{2}-\d{2}$/.test(edited) ? edited : utcDay(edited);
  if (!day || day === utcDay(current)) return undefined;
  return noonUtcMillis(day) ?? undefined;
};

/** True when the violation is open and its due day is before today's UTC day. */
export const isOverdue = (v: ViolationReturn, now: Date = new Date()): boolean => {
  const due = utcDay(v.due_date);
  if (!due || normalizeStatus(v.status) === 'Resolved') return false;
  return due < now.toISOString().slice(0, 10);
};

/** A short, stable reference for a violation — the tail of its public id. */
export const shortRef = (id: string): string =>
  `VIO-${id.replace(/^vio_/, '').slice(0, 5).toUpperCase()}`;
